import React, { useState } from 'react';
import type { CarouselCard, TemplateButton } from '../../types/message';

interface CarouselCardsProps {
  cards: CarouselCard[];
  onButtonClick?: (button: TemplateButton, cardIndex: number) => void;
}

const ImagePlaceholder = () => (
  <div className="w-full h-24 bg-cream flex items-center justify-center">
    <svg className="w-8 h-8 text-graphite/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  </div>
);

const PlayOverlay = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <div className="bg-black/50 rounded-full p-1.5">
      <svg className="w-4 h-4 text-warm-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  </div>
);

const CarouselCardItem: React.FC<{
  card: CarouselCard;
  onButtonClick?: (button: TemplateButton) => void;
}> = ({ card, onButtonClick }) => {
  const [imageError, setImageError] = useState(false);
  const isVideo = card.header_format === 'VIDEO';

  return (
    <div className="flex-shrink-0 w-[160px] bg-warm-white border border-sketch rounded-lg overflow-hidden shadow-sketch">
      {/* Card Header Media */}
      {card.header_media_url && !imageError ? (
        <div className="relative">
          <img
            src={card.header_media_url}
            alt={card.title || 'Card'}
            className="w-full h-24 object-cover"
            onError={() => setImageError(true)}
          />
          {isVideo && <PlayOverlay />}
        </div>
      ) : (
        <ImagePlaceholder />
      )}

      {/* Card Content */}
      <div className="p-2">
        {card.title && (
          <p className="text-sm font-semibold text-charcoal line-clamp-1 mb-0.5">
            {card.title}
          </p>
        )}
        {card.body && (
          <p className="text-xs text-graphite line-clamp-2">
            {card.body}
          </p>
        )}
      </div>

      {/* Card Buttons */}
      {card.buttons && card.buttons.length > 0 && (
        <div className="border-t border-sketch-soft">
          {card.buttons.slice(0, 2).map((btn, idx) => (
            <button
              key={idx}
              onClick={() => onButtonClick?.(btn)}
              className="w-full py-2 text-center border-b border-sketch-soft last:border-b-0 text-xs text-[#00a884] font-medium hover:bg-cream transition-colors"
            >
              {btn.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const CarouselCards: React.FC<CarouselCardsProps> = ({
  cards,
  onButtonClick,
}) => {
  return (
    <div className="mt-2 -mx-1">
      <div className="flex gap-2 overflow-x-auto pb-2 px-1">
        {cards.map((card, idx) => (
          <CarouselCardItem
            key={card.card_index ?? idx}
            card={card}
            onButtonClick={(btn) => onButtonClick?.(btn, card.card_index ?? idx)}
          />
        ))}
      </div>
    </div>
  );
};

export default CarouselCards;
